import React from "react";
import { FormSection } from "../../../common/FormComponents";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import Tooltip from "../../../common/FormComponents/Tooltip";

type MembershipDetails = {
  hearAboutSource: string;
  reasonsForJoining: string;
  boardInterest: string;
};

type MembershipDetailsSectionProps = {
  membershipDetails: MembershipDetails;
  onChange: ({
    field,
    value,
  }: {
    field: keyof MembershipDetails;
    value: string;
  }) => void;
  errors?: Record<string, string>;
};

const hearAboutSources = [
  { value: "Search Engine", label: "Search Engine" },
  { value: "Social Media", label: "Social Media" },
  { value: "Word of Mouth", label: "Word of Mouth" },
  { value: "Email", label: "Email" },
  { value: "Event", label: "Event" },
  { value: "Advertisement", label: "Advertisement" },
  { value: "Referral", label: "Referral" },
  { value: "Other", label: "Other" },
];

const reasonsForJoining = [
  { value: "Networking", label: "Networking" },
  { value: "Business Promotion", label: "Business Promotion" },
  { value: "Educational Resources", label: "Educational Resources" },
  { value: "Community Involvement", label: "Community Involvement" },
  { value: "Policy Advocacy", label: "Policy Advocacy" },
  { value: "Member Benefits", label: "Member Benefits" },
  { value: "Other", label: "Other" },
];

const MembershipDetailsSection = ({
  membershipDetails,
  onChange,
  errors = {},
}: MembershipDetailsSectionProps) => {
  const handleSelectChange =
    (field: keyof MembershipDetails) => (e: SelectChangeEvent<string>) => {
      onChange({ field, value: e.target.value });
    };

  const handleRadioChange =
    (field: keyof MembershipDetails) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field, value: e.target.value });
    };

  return (
    <FormSection
      title="Membership Details"
      tooltipText="Additional information about the membership"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <div className="flex items-center mb-1">
            <label
              htmlFor="hearAboutSource"
              className="block text-sm font-medium text-gray-900"
            >
              How did they hear about us?
            </label>
            <Tooltip content="Select how the member heard about the chamber" />
          </div>
          <Select
            id="hearAboutSource"
            name="hearAboutSource"
            value={membershipDetails.hearAboutSource}
            onChange={handleSelectChange("hearAboutSource")}
            displayEmpty
            fullWidth
            error={!!errors.hearAboutSource}
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 1,
                borderColor: "#E1A731",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d32f2f",
              },
              "& .MuiSelect-select": {
                fontSize: 16,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {hearAboutSources.map((source) => (
              <MenuItem key={source.value} value={source.value}>
                {source.label}
              </MenuItem>
            ))}
          </Select>
          {errors.hearAboutSource && (
            <p className="text-red-500 text-xs mt-1">
              {errors.hearAboutSource}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center mb-1">
            <label
              htmlFor="reasonsForJoining"
              className="block text-sm font-medium text-gray-900"
            >
              Primary reason for joining
            </label>
            <Tooltip content="Select the member's primary motivation for joining" />
          </div>
          <Select
            id="reasonsForJoining"
            name="reasonsForJoining"
            value={membershipDetails.reasonsForJoining}
            onChange={handleSelectChange("reasonsForJoining")}
            displayEmpty
            fullWidth
            error={!!errors.reasonsForJoining}
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 1,
                borderColor: "#E1A731",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d32f2f",
              },
              "& .MuiSelect-select": {
                fontSize: 16,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select an option
            </MenuItem>
            {reasonsForJoining.map((reason) => (
              <MenuItem key={reason.value} value={reason.value}>
                {reason.label}
              </MenuItem>
            ))}
          </Select>
          {errors.reasonsForJoining && (
            <p className="text-red-500 text-xs mt-1">
              {errors.reasonsForJoining}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <FormControl component="fieldset">
            <div className="flex items-center mb-1">
              <FormLabel
                component="legend"
                className="text-sm font-medium text-gray-900"
              >
                Is this member interested in serving on the Board of Directors
                or participating in committees?
              </FormLabel>
              <Tooltip content="Indicates the member's level of interest in participating on the Chamber's board or committees" />
            </div>
            <RadioGroup
              row
              name="boardInterest"
              value={membershipDetails.boardInterest}
              onChange={handleRadioChange("boardInterest")}
            >
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
              <FormControlLabel
                value="Maybe"
                control={<Radio />}
                label="Maybe"
              />
            </RadioGroup>
          </FormControl>
          {errors.boardInterest && (
            <p className="text-red-500 text-xs mt-1">{errors.boardInterest}</p>
          )}
        </div>
      </div>
    </FormSection>
  );
};

export default MembershipDetailsSection;
